import { useState } from 'react';
import { motion } from 'framer-motion';
import API_URL from '../config/api.js';
import axios from 'axios';
import '../styles/MyReferrals.css';


function MyReferrals() {
  const [step, setStep] = useState('email'); // email, code, referrals
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sessionToken, setSessionToken] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [isEmployee, setIsEmployee] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/api/auth/send-code`, { email });
      setIsEmployee(response.data.isEmployee);
      setStep('code');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/api/auth/verify-code`, { email, code });
      setSessionToken(response.data.sessionToken);
      setIsEmployee(response.data.isEmployee);
      
      // Fetch referrals
      const referralsResponse = await axios.get(`${API_URL}/api/my-referrals`, {
        headers: { Authorization: `Bearer ${response.data.sessionToken}` },
      });
      
      setReferrals(referralsResponse.data.referrals);
      setStep('referrals');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#F6AD55',
      contacted: '#4299E1',
      qualified: '#48BB78',
      converted: '#38B2AC',
      declined: '#FC8181',
    };
    return colors[status] || '#A0AEC0';
  };

  return (
    <div className="my-referrals-page">
      <div className="my-referrals-hero">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="hero-content"
        >
          <img 
            src="https://i.imgur.com/DAg1a53.png" 
            alt="1Solar Logo" 
            className="hero-logo"
          />
          <h1>My Referrals</h1>
          <p>View and track your referral submissions</p>
        </motion.div>
      </div>

      <div className="container">
        {step === 'email' && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="verification-card"
          >
            <h2>Enter Your Email</h2>
            <p>We'll send you a verification code to access your referrals</p>
            
            {error && <div className="error-message">{error}</div>}
            
            <form onSubmit={handleSendCode}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                required
                className="email-input"
              />
              <button type="submit" disabled={loading} className="submit-button">
                {loading ? 'Sending...' : 'Send Verification Code'}
              </button>
            </form>
          </motion.div>
        )}

        {step === 'code' && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="verification-card"
          >
            <h2>Enter Verification Code</h2>
            <p>Check your email for the 6-digit code we sent to {email}</p>
            
            {error && <div className="error-message">{error}</div>}
            
            <form onSubmit={handleVerifyCode}>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                required
                maxLength={6}
                className="code-input"
                autoComplete="off"
              />
              <button type="submit" disabled={loading} className="submit-button">
                {loading ? 'Verifying...' : 'Verify Code'}
              </button>
              <button 
                type="button" 
                onClick={() => setStep('email')} 
                className="back-button"
              >
                Back
              </button>
            </form>
          </motion.div>
        )}

        {step === 'referrals' && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="referrals-container"
          >
            <div className="referrals-header">
              <h2>
                {isEmployee ? 'All Referrals' : 'Your Referrals'}
                <span className="count">({referrals.length})</span>
              </h2>
              <p className="email-display">Logged in as: {email}</p>
            </div>

            {referrals.length === 0 ? (
              <div className="no-referrals">
                <p>No referrals found</p>
              </div>
            ) : (
              <div className="referrals-grid">
                {referrals.map((referral) => (
                  <motion.div
                    key={referral._id}
                    className="referral-card"
                    whileHover={{ scale: 1.02 }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <div className="referral-header">
                      <div>
                        <h3>{referral.lead.name}</h3>
                        <p className="referral-type">{referral.referralType}</p>
                      </div>
                      <div 
                        className="status-badge"
                        style={{ backgroundColor: getStatusColor(referral.status) }}
                      >
                        {referral.status}
                      </div>
                    </div>

                    <div className="referral-details">
                      <div className="detail-row">
                        <span className="label">Products:</span>
                        <span className="value">{referral.products.join(', ')}</span>
                      </div>
                      <div className="detail-row">
                        <span className="label">Email:</span>
                        <span className="value">{referral.lead.email}</span>
                      </div>
                      <div className="detail-row">
                        <span className="label">Phone:</span>
                        <span className="value">{referral.lead.phone}</span>
                      </div>
                      <div className="detail-row">
                        <span className="label">Location:</span>
                        <span className="value">
                          {referral.lead.city}, {referral.lead.state}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="label">Submitted:</span>
                        <span className="value">{formatDate(referral.createdAt)}</span>
                      </div>
                    </div>

                    {referral.formsSent.length > 0 && (
                      <div className="forms-section">
                        <h4>Forms Sent:</h4>
                        {referral.formsSent.map((form, idx) => (
                          <div key={idx} className="form-status">
                            <span>{form.productId}</span>
                            <span className={form.completed ? 'completed' : 'pending'}>
                              {form.completed ? '✓ Completed' : '⏳ Pending'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {referral.customerResponses.length > 0 && (
                      <div className="responses-section">
                        <p className="response-indicator">
                          ✓ Customer form completed
                        </p>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default MyReferrals;